import { decodePolyline } from "@/lib/utils"
import { haversineKm, projectOnPathKm, buildPathFromRide, computeSegmentPrice } from "@/lib/route-matching"

// awaiting_payment: pagamento de cartão em análise no MP — assento fica
// reservado para o passageiro até a conclusão da análise.
export const ACTIVE_RESERVATION_STATUSES = ["pending", "confirmed", "awaiting_payment"] as const

export interface RoutePoint {
  index: number
  name: string
  cityName?: string | null
  lat: number
  lng: number
  price?: number
  kind: "origin" | "stop" | "destination"
}

export interface SegmentReservation {
  reservationId: string
  seatId: string | null
  status: string
  boardStopIndex: number
  alightStopIndex: number
  boardKm?: number
  alightKm?: number
}

export interface SegmentSeat {
  id: string
  row?: number
  side?: string
  available?: boolean
}

function locationPoint(loc: any): { lat: number; lng: number } | null {
  if (loc?.location?.coordinates && loc.location.coordinates.length >= 2) {
    return { lat: loc.location.coordinates[1], lng: loc.location.coordinates[0] }
  }
  if (typeof loc?.lat === "number" && typeof loc?.lng === "number") {
    return { lat: loc.lat, lng: loc.lng }
  }
  return null
}

export function getRoutePoints(ride: any): RoutePoint[] {
  const points: RoutePoint[] = []
  const originPt = locationPoint(ride?.origin)
  if (originPt) {
    points.push({
      index: 0,
      name: ride.origin.name || "Origem",
      lat: originPt.lat,
      lng: originPt.lng,
      price: ride.price,
      kind: "origin",
    })
  }
  const stops = Array.isArray(ride?.stops)
    ? [...ride.stops].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    : []
  for (const stop of stops) {
    const p = locationPoint(stop)
    if (!p) continue
    points.push({
      index: points.length,
      name: stop.name || "Parada",
      cityName: (stop as any)?.cityName ?? null,
      lat: p.lat,
      lng: p.lng,
      price: stop.price,
      kind: "stop",
    })
  }
  const destPt = locationPoint(ride?.destination)
  if (destPt) {
    points.push({
      index: points.length,
      name: ride.destination.name || "Destino",
      lat: destPt.lat,
      lng: destPt.lng,
      price: ride.price,
      kind: "destination",
    })
  }
  return points
}

export function routePointIndexFor(
  ride: any,
  points: RoutePoint[],
  lat: number,
  lng: number,
  maxKm: number
): number {
  let best = -1
  let bestDist = maxKm
  for (const p of points) {
    const d = haversineKm(lat, lng, p.lat, p.lng)
    if (d <= bestDist) {
      bestDist = d
      best = p.index
    }
  }
  return best
}

export function isActiveStatus(status: string): boolean {
  return ACTIVE_RESERVATION_STATUSES.includes(status as any)
}

export function segmentsInTrecho(boardStopIndex: number, alightStopIndex: number): number[] {
  const segments: number[] = []
  for (let i = boardStopIndex; i < alightStopIndex; i++) segments.push(i)
  return segments
}

export function trechosOverlap(
  aBoard: number,
  aAlight: number,
  bBoard: number,
  bAlight: number
): boolean {
  const aSegments = segmentsInTrecho(aBoard, aAlight)
  const bSegments = new Set(segmentsInTrecho(bBoard, bAlight))
  return aSegments.some((s) => bSegments.has(s))
}

// boardKm/alightKm são armazenados com 3 casas decimais e o alongKm é
// reprojetado do polyline a cada busca — diferenças de centímetros entre um
// desembarque e o embarque seguinte NÃO são sobreposição real.
export const KM_OVERLAP_EPSILON_KM = 0.05

export function trechoOverlapByKm(aBoardKm: number, aAlightKm: number, bBoardKm: number, bAlightKm: number): boolean {
  return (
    aBoardKm < bAlightKm - KM_OVERLAP_EPSILON_KM &&
    bBoardKm < aAlightKm - KM_OVERLAP_EPSILON_KM
  )
}

export function reservationIntervalKm(r: SegmentReservation, alongKm: number[]): [number, number] {
  if (typeof r.boardKm === "number" && typeof r.alightKm === "number") {
    return [r.boardKm, r.alightKm]
  }
  const board = alongKm[r.boardStopIndex] ?? 0
  const alight = alongKm[r.alightStopIndex] ?? alongKm[alongKm.length - 1] ?? board
  return [board, alight]
}

export function seatAvailableForKm(
  seatId: string | null,
  reservations: SegmentReservation[],
  boardKm: number,
  alightKm: number,
  alongKm: number[],
  baseAvailable = true
): boolean {
  if (!seatId) return true
  if (!baseAvailable) return false
  return !reservations.some(
    (r) =>
      r.seatId === seatId &&
      isActiveStatus(r.status) &&
      trechoOverlapByKm(boardKm, alightKm, ...reservationIntervalKm(r, alongKm))
  )
}

export function computeTrechoAvailabilityByKm(
  seats: SegmentSeat[],
  reservations: SegmentReservation[],
  boardKm: number,
  alightKm: number,
  alongKm: number[]
): { totalSeats: number; freeSeats: number; availableSeatIds: string[] } {
  const totalSeats = seats.length
  const baseAvailable = seats.filter((s) => s.available !== false)
  const availableSeatIds = baseAvailable
    .filter((s) => seatAvailableForKm(s.id, reservations, boardKm, alightKm, alongKm))
    .map((s) => s.id)
  return { totalSeats, freeSeats: availableSeatIds.length, availableSeatIds }
}

export function seatAvailableForTrecho(
  seatId: string | null,
  reservations: SegmentReservation[],
  boardStopIndex: number,
  alightStopIndex: number,
  baseAvailable = true
): boolean {
  if (!seatId) return true
  if (!baseAvailable) return false
  return !reservations.some(
    (r) =>
      r.seatId === seatId &&
      isActiveStatus(r.status) &&
      trechosOverlap(r.boardStopIndex, r.alightStopIndex, boardStopIndex, alightStopIndex)
  )
}

// "Esta poltrona já é MINHA neste trecho?" — base do guard de idempotência da
// reserva direta (duplo clique). Olha só as reservas `mine` do usuário chamador.
// É por TRECHO e não por poltrona: o modelo permite ao mesmo usuário dois
// trechos não sobrepostos da mesma carona (hasTemporalConflict ignora reservas
// da própria carona exatamente por isso), e um guard só-por-seatId engoliria
// legítimo trecho deslocado.
export function seatHeldByUserInTrecho(
  ride: any,
  seat: {
    seatId: string
    boardStopIndex?: number
    alightStopIndex?: number
    boardKm?: number
    alightKm?: number
  },
  mine: SegmentReservation[]
): boolean {
  if (!seat.seatId || !mine.length) return false
  const isTurbo = !!ride?.turboMode
  const hasKm =
    isTurbo && typeof seat.boardKm === "number" && typeof seat.alightKm === "number"
  if (hasKm) {
    return !seatAvailableForKm(
      seat.seatId,
      mine,
      seat.boardKm as number,
      seat.alightKm as number,
      cumulativeKmAtRoutePoints(ride, getRoutePoints(ride)),
      true
    )
  }
  return !seatAvailableForTrecho(
    seat.seatId,
    mine,
    seat.boardStopIndex ?? -1,
    seat.alightStopIndex ?? -1,
    true
  )
}

export function computeTrechoAvailability(
  seats: SegmentSeat[],
  reservations: SegmentReservation[],
  boardStopIndex: number,
  alightStopIndex: number
): { totalSeats: number; freeSeats: number; availableSeatIds: string[] } {
  const totalSeats = seats.length
  const baseAvailable = seats.filter((s) => s.available !== false)
  const availableSeatIds = baseAvailable
    .filter((s) => seatAvailableForTrecho(s.id, reservations, boardStopIndex, alightStopIndex))
    .map((s) => s.id)
  return { totalSeats, freeSeats: availableSeatIds.length, availableSeatIds }
}

export function getSeatOccupancyMap(
  seats: SegmentSeat[],
  reservations: SegmentReservation[]
): Map<string, SegmentReservation[]> {
  const map = new Map<string, SegmentReservation[]>()
  for (const seat of seats) {
    map.set(
      seat.id,
      reservations.filter(
        (r) => r.seatId === seat.id && isActiveStatus(r.status)
      )
    )
  }
  return map
}

export function cumulativeKmAtRoutePoints(ride: any, points: RoutePoint[]): number[] {
  const storedTotal = ride?.route?.totalKm
  const storedStops: any[] = Array.isArray(ride?.stops) ? ride.stops : []
  const stopPointCount = points.filter((p) => p.kind === "stop").length
  const hasStoredData =
    typeof storedTotal === "number" &&
    storedStops.length === stopPointCount &&
    storedStops.every((s) => typeof s?.alongKm === "number")

  if (hasStoredData) {
    const sortedStops = [...storedStops].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    let stopIdx = 0
    return points.map((p) => {
      if (p.kind === "origin") return 0
      if (p.kind === "destination") return storedTotal
      return sortedStops[stopIdx++]?.alongKm ?? 0
    })
  }

  let path: Array<{ lat: number; lng: number }> = []
  if (ride?.route?.polyline) {
    try {
      path = decodePolyline(ride.route.polyline)
    } catch {
      path = []
    }
  }
  if (path.length < 2 && Array.isArray(ride?.route?.sampledCoordinates)) {
    path = ride.route.sampledCoordinates.map((p: any) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length < 2 && points.length >= 2) {
    path = points.map((p) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length >= 2) {
    if (points[0]) path[0] = { lat: points[0].lat, lng: points[0].lng }
    const last = points[points.length - 1]
    if (last) path[path.length - 1] = { lat: last.lat, lng: last.lng }
  }
  if (path.length < 2) return points.map(() => 0)

  const alongKm: number[] = []
  for (const p of points) {
    const proj = projectOnPathKm({ lat: p.lat, lng: p.lng }, path)
    alongKm.push(proj ? proj.alongKm : 0)
  }
  return alongKm
}

export function trechoPolylinePoints(
  ride: any,
  points: RoutePoint[],
  alongKm: number[],
  boardStopIndex: number,
  alightStopIndex: number
): Array<{ lat: number; lng: number }> {
  let path: Array<{ lat: number; lng: number }> = []
  if (ride?.route?.polyline) {
    try {
      path = decodePolyline(ride.route.polyline)
    } catch {
      path = []
    }
  }
  if (path.length < 2 && Array.isArray(ride?.route?.sampledCoordinates)) {
    path = ride.route.sampledCoordinates.map((p: any) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length < 2 && points.length >= 2) {
    path = points.map((p) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length < 2) return []

  const fromKm = alongKm[boardStopIndex] ?? 0
  const toKm = alongKm[alightStopIndex] ?? alongKm[alongKm.length - 1] ?? fromKm

  const out: Array<{ lat: number; lng: number }> = []
  let cumulative = 0
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const segKm = haversineKm(a.lat, a.lng, b.lat, b.lng)
    const nextCumulative = cumulative + segKm

    const segEndBeforeFrom = nextCumulative <= fromKm
    const segStartAfterTo = cumulative >= toKm
    if (!segEndBeforeFrom && !segStartAfterTo) {
      if (out.length === 0 || out[out.length - 1] !== a) {
        if (nextCumulative > fromKm) out.push(a)
      }
      out.push(b)
    }
    cumulative = nextCumulative
  }

  if (out.length < 2) {
    const clamped = Math.min(points.length - 1, Math.max(0, alightStopIndex))
    const start = Math.min(points.length - 1, Math.max(0, boardStopIndex))
    for (let i = start; i <= clamped; i++) {
      out.push({ lat: points[i].lat, lng: points[i].lng })
    }
  }
  return out
}

export function trechoPolylinePointsFromKm(
  ride: any,
  fromKm: number,
  toKm: number
): Array<{ lat: number; lng: number }> {
  let path: Array<{ lat: number; lng: number }> = []
  if (ride?.route?.polyline) {
    try {
      path = decodePolyline(ride.route.polyline)
    } catch {
      path = []
    }
  }
  if (path.length < 2 && Array.isArray(ride?.route?.sampledCoordinates)) {
    path = ride.route.sampledCoordinates.map((p: any) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length < 2) return []

  const out: Array<{ lat: number; lng: number }> = []
  let cumulative = 0
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const segKm = haversineKm(a.lat, a.lng, b.lat, b.lng)
    const nextCumulative = cumulative + segKm

    const segEndBeforeFrom = nextCumulative <= fromKm
    const segStartAfterTo = cumulative >= toKm
    if (!segEndBeforeFrom && !segStartAfterTo) {
      if (out.length === 0 || out[out.length - 1] !== a) {
        if (nextCumulative > fromKm) out.push(a)
      }
      out.push(b)
    }
    cumulative = nextCumulative
  }

  if (out.length < 2) {
    const points = getRoutePoints(ride)
    const alongKm = cumulativeKmAtRoutePoints(ride, points)
    points.forEach((p, i) => {
      const km = alongKm[i] ?? 0
      if (km >= fromKm - 0.001 && km <= toKm + 0.001) {
        out.push({ lat: p.lat, lng: p.lng })
      }
    })
  }
  return out
}

export function segmentPriceForTrecho(ride: any, boardStopIndex: number, alightStopIndex: number): number {
  const points = getRoutePoints(ride)
  if (points.length < 2) return ride?.price || 0
  const alongKm = cumulativeKmAtRoutePoints(ride, points)
  const boardKm = alongKm[boardStopIndex] ?? 0
  const alightKm = alongKm[alightStopIndex] ?? alongKm[alongKm.length - 1] ?? boardKm
  const path = buildPathFromRide(ride)
  return computeSegmentPrice(ride, boardKm, alightKm, path)
}

export interface TrechoSeatInput {
  boardStopIndex: number
  alightStopIndex: number
  boardKm?: number | null
  alightKm?: number | null
}

/**
 * Valor do TRECHO de um assento — a fonte única de preço da reserva.
 *
 * Carona contínua (turbo) precifica por KM (boardKm→alightKm sobre a geometria
 * da rota); carona por paradas precifica pelo par de índices. É exatamente a
 * mesma conta usada em `POST /api/rides/[id]/reserve` e no fluxo de pagamento —
 * manter aqui evita divergência entre quem grava a reserva (com ou sem
 * monetização) e quem exibe o valor (resumo da viagem, chat, conforto).
 */
export function trechoPriceForSeat(ride: any, seat: TrechoSeatInput): number {
  const isTurbo = !!ride?.turboMode
  if (isTurbo && typeof seat.boardKm === "number" && typeof seat.alightKm === "number") {
    return computeSegmentPrice(ride, seat.boardKm, seat.alightKm, buildPathFromRide(ride))
  }
  return segmentPriceForTrecho(ride, seat.boardStopIndex, seat.alightStopIndex)
}

/**
 * Tolerância para casar um km projetado (polyline, 3 casas) com o `alongKm`
 * de um ponto da rota (arredondado, 2 casas). Diferença de poucos metros é o
 * MESMO ponto — sem isso, destino na parada "Cascavel" (135.403 vs 135.4)
 * saltava para o próximo ponto e gravava alightStopIndex errado na reserva.
 * Mesmo valor de `COINCIDENT_EPSILON_KM` em `lib/route-timeline.ts`.
 */
export const STOP_SNAP_EPSILON_KM = 0.05

export function nextStopIndexAtOrAfter(alongKm: number[], targetKm: number): number {
  for (let i = 0; i < alongKm.length; i++) {
    if (alongKm[i] >= targetKm - STOP_SNAP_EPSILON_KM) return i
  }
  return alongKm.length - 1
}

export function prevStopIndexAtOrBefore(alongKm: number[], targetKm: number): number {
  for (let i = alongKm.length - 1; i >= 0; i--) {
    if (alongKm[i] <= targetKm + STOP_SNAP_EPSILON_KM) return i
  }
  return 0
}
